<template>
  <VaModal
    class="z-modal!"
    :model-value="modelValue"
    :title="title"
    :size="vuesticSize"
    :hide-default-actions="true"
    :no-outside-dismiss="!closeOnOutsideClick"
    :no-esc-dismiss="!closeOnEsc"
    @update:model-value="onUpdateModelValue"
  >
    <template v-if="$slots['header']" #header>
      <slot name="header" />
    </template>
    <div ref="dialogRef">
      <slot />
    </div>
    <template v-if="$slots['footer']" #footer>
      <slot name="footer" />
    </template>
  </VaModal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { VaModal } from 'vuestic-ui'

import { useFocusTrap } from '@/shared/composables'

export interface AppModalProps {
  readonly modelValue: boolean
  readonly title?: string
  readonly size?: 'sm' | 'md' | 'lg'
  readonly closeOnOutsideClick?: boolean
  readonly closeOnEsc?: boolean
}

const props = withDefaults(defineProps<AppModalProps>(), {
  title: '',
  size: 'md',
  closeOnOutsideClick: true,
  closeOnEsc: true,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const vuesticSize = computed<'small' | 'medium' | 'large'>(() => {
  switch (props.size) {
    case 'sm':
      return 'small'
    case 'lg':
      return 'large'
    case 'md':
    default:
      return 'medium'
  }
})

function onUpdateModelValue(value: boolean): void {
  emit('update:modelValue', value)
}

const dialogRef = ref<HTMLDivElement | null>(null)
const { activate, deactivate } = useFocusTrap(dialogRef)

/**
 * Mirror Vuestic's open/close state into the existing `useFocusTrap` registry
 * so stacked modals continue to coordinate correctly. The trap is suspended
 * when the dialog hides, then reactivated on next tick (so Vuestic's teleport
 * has time to mount the DOM node the trap points at).
 */
watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      await nextTick()
      activate()
    } else {
      deactivate()
    }
  },
)
</script>
